import React, { useState, useEffect, useCallback } from 'react';
import { homework } from '../../api';
import ProgressBar from './ProgressBar';
import './Homework.css';

const REFRESH_MS = 30 * 1000;

const dueText = (d) => new Date(d).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
const isLate = (hw) => hw.dueDate && !hw.completedAt && new Date(hw.dueDate) < new Date();

// Player: the homework from my coach(es). Tap a task to tick it off (tap again to take the tick back).
const PlayerHomework = ({ onOpenCountChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const report = useCallback((list) => {
    if (!onOpenCountChange) return;
    onOpenCountChange(list.filter(h => !h.completedAt).reduce((n, h) => n + h.items.filter(i => !i.done).length, 0));
  }, [onOpenCountChange]);

  const load = useCallback(() => {
    return homework.getMine()
      .then(res => { setData(res.data); report(res.data.homework || []); })
      .catch(() => setData(prev => prev || { homework: [], total: 0, done: 0, percent: 0 }));
  }, [report]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const tick = async (hw, item) => {
    if (busy) return;
    const next = !item.done;
    setBusy(`${hw._id}:${item._id}`);
    setError('');
    // Show the tick at once; the server's answer replaces it
    setData(prev => {
      const list = prev.homework.map(h => {
        if (h._id !== hw._id) return h;
        const items = h.items.map(i => (i._id === item._id ? { ...i, done: next } : i));
        const done = items.filter(i => i.done).length;
        return { ...h, items, done, percent: Math.round((done / items.length) * 100), completedAt: done === items.length ? new Date().toISOString() : null };
      });
      return { ...prev, homework: list, ...overall(list) };
    });
    try {
      await homework.setDone(hw._id, item._id, next);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save that. Try again.');
      load();
    } finally {
      setBusy('');
    }
  };

  if (data === null) return <div className="hw-wrap"><div className="hw-sub">Loading…</div></div>;

  const open = data.homework.filter(h => !h.completedAt);
  const finished = data.homework.filter(h => h.completedAt);

  if (data.homework.length === 0) {
    return (
      <div className="hw-wrap">
        <div className="hw-empty">
          <div style={{ fontSize: 40 }}>📚</div>
          <strong>No homework right now</strong>
          <div className="hw-sub" style={{ marginTop: 4 }}>When your coach gives you something to practise, it shows up here.</div>
        </div>
      </div>
    );
  }

  const card = (hw) => (
    <div key={hw._id} className={`hw-card${hw.completedAt ? ' hw-assign--done' : ''}`}>
      <div className="hw-head">
        <h3 className="hw-h">{hw.title || 'Homework'}</h3>
        {hw.completedAt
          ? <span className="hw-pill hw-pill--ok">Done</span>
          : isLate(hw) ? <span className="hw-pill hw-pill--late">Overdue</span>
          : hw.dueDate ? <span className="hw-pill">Due {dueText(hw.dueDate)}</span> : null}
      </div>
      <div className="hw-sub" style={{ marginBottom: 8 }}>From {hw.fromName} · {hw.done} of {hw.total} done</div>
      <ProgressBar percent={hw.percent} total={hw.total} />
      {hw.note && <div className="bq-msg" style={{ marginTop: 10 }}>{hw.note}</div>}
      <div style={{ marginTop: 6 }}>
        {hw.items.map(item => (
          <button
            key={item._id}
            type="button"
            className="hw-task"
            aria-pressed={item.done}
            disabled={busy === `${hw._id}:${item._id}`}
            onClick={() => tick(hw, item)}
          >
            <span className="hw-check" aria-hidden="true">{item.done ? '✓' : ''}</span>
            <span className="hw-task-text">{item.text}</span>
          </button>
        ))}
      </div>
      {hw.completedAt && <div className="hw-banner" style={{ marginTop: 8 }}>🎉 All done, well played!</div>}
    </div>
  );

  return (
    <div className="hw-wrap">
      <div className="hw-card">
        <div className="hw-head">
          <h3 className="hw-h">My progress</h3>
          <span className="hw-sub">{data.done} of {data.total} tasks</span>
        </div>
        <ProgressBar percent={data.percent} total={data.total} />
      </div>
      {error && <p className="tl-error" style={{ textAlign: 'center' }}>{error}</p>}
      {open.map(card)}
      {finished.length > 0 && <div className="br-section" style={{ margin: '6px 4px 0' }}>Finished</div>}
      {finished.map(card)}
    </div>
  );
};

function overall(list) {
  const total = list.reduce((n, h) => n + h.items.length, 0);
  const done = list.reduce((n, h) => n + h.items.filter(i => i.done).length, 0);
  return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
}

export default PlayerHomework;
